/**
 * Related Transactions Sidebar Component
 * TASK-213: Build Transaction Detail Modal
 *
 * Displays related transactions for pattern context
 * Mock implementation - will integrate with backend API in future
 */

import React, { useMemo } from 'react';
import { Clock, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { formatCurrency } from '../../utils/formatters';

interface RelatedTransactionsSidebarProps {
  /** Client ID */
  clientId?: string;
}

interface MockRelatedTransaction {
  id: string;
  amount: number;
  currency: string;
  daysAgo: number;
  type: string;
}

const RelatedTransactionsSidebar: React.FC<RelatedTransactionsSidebarProps> = ({
  clientId,
}) => {
  // Generate mock related transactions
  const mockRelatedTransactions = useMemo((): MockRelatedTransaction[] => {
    // In real app, this would be fetched from API based on clientId
    // For prototype, generate 3-4 mock transactions
    if (!clientId) return [];

    return [
      {
        id: 'TRX-2025-001230',
        amount: 45000,
        currency: 'CHF',
        daysAgo: 7,
        type: 'wire_transfer_out',
      },
      {
        id: 'TRX-2025-001225',
        amount: 52000,
        currency: 'CHF',
        daysAgo: 14,
        type: 'wire_transfer_out',
      },
      {
        id: 'TRX-2025-001218',
        amount: 38000,
        currency: 'CHF',
        daysAgo: 21,
        type: 'wire_transfer_out',
      },
    ];
  }, [clientId]);

  // Calculate pattern analysis
  const patternAnalysis = useMemo(() => {
    if (mockRelatedTransactions.length === 0) return null;

    const avgAmount =
      mockRelatedTransactions.reduce((sum, t) => sum + t.amount, 0) /
      mockRelatedTransactions.length;

    return {
      count: mockRelatedTransactions.length,
      avgAmount,
      timeframe: `${mockRelatedTransactions[mockRelatedTransactions.length - 1].daysAgo} days`,
    };
  }, [mockRelatedTransactions]);

  // Format time ago
  const formatTimeAgo = (daysAgo: number): string => {
    if (daysAgo === 0) return 'Today';
    if (daysAgo === 1) return '1 day ago';
    if (daysAgo < 7) return `${daysAgo} days ago`;
    if (daysAgo < 14) return '1 week ago';
    if (daysAgo < 21) return '2 weeks ago';
    if (daysAgo < 30) return '3 weeks ago';
    return `${Math.floor(daysAgo / 30)} months ago`;
  };

  if (!clientId || mockRelatedTransactions.length === 0) {
    return (
      <Card className="border border-gray-200">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">
            Related Transactions
          </h3>
          <p className="text-sm text-gray-500">
            No related transactions found for this client.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-gray-200">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">
            Related Transactions
          </h3>
        </div>

        {/* Transaction List */}
        <div className="space-y-2 mb-4">
          {mockRelatedTransactions.map((transaction) => (
            <div
              key={transaction.id}
              className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={() => {
                console.log('View transaction:', transaction.id);
                // TODO: Open modal for this transaction
              }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-gray-900">
                  {formatCurrency(transaction.amount, transaction.currency)}
                </span>
                <span className="text-xs text-gray-500">
                  {formatTimeAgo(transaction.daysAgo)}
                </span>
              </div>
              <span className="text-xs text-gray-600">{transaction.id}</span>
            </div>
          ))}
        </div>

        {/* Pattern Summary */}
        {patternAnalysis && (
          <div className="pt-3 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
              <h4 className="text-sm font-semibold text-gray-900">Pattern Analysis</h4>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-700">
                <span className="font-medium">{patternAnalysis.count}</span> transactions
                in <span className="font-medium">{patternAnalysis.timeframe}</span>
              </p>
              <p className="text-sm text-gray-700">
                Average:{' '}
                <span className="font-medium">
                  {formatCurrency(patternAnalysis.avgAmount, 'CHF')}
                </span>
              </p>
              <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                ⚠️ Higher frequency and volume than client's historical pattern
              </div>
            </div>
          </div>
        )}

        {/* Info Note */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Click a transaction to view details (prototype: feature coming soon)
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default RelatedTransactionsSidebar;
